<template>
  <div
    v-if="isCSSLoaded"
    id="tripla-chat-window-container"
    class="triplabot-window notranslate"
    :dir="isRTL ? 'rtl' : 'ltr'"
    @dragover="onDragover"
    @dragleave="onDragleave"
    @drop="onDrop"
    @click="clickOutside"
  >
    <!-- Header -->
    <header v-if="!isApp && liveMode && displayHeader" class="chatbot-header">
      <h1 hidden>tripla chatbot service</h1>
      <div class="op" :class="{ [style['no-login']]: hideSignIn }">
        <div v-if="isBranchChat()" class="btn-back" @click="switchBackToBranch"></div>
        <div class="img" :style="conciergeIcon" @click="popupBranchSelector">
          <!-- This div is using background, check css and use style tags for dynamic stuff -->
          <span v-if="displayCount()" class="chat-count">+{{ chatCount }}</span>
        </div>
        <div class="text">
          <h2 class="hotel-title">{{ facilityName }}</h2>
          <p class="operator-title">{{ facilityOperator }}</p>
        </div>
        <template v-if="!appStore.isFullScreen">
          <icon-collapse-content v-if="maximizedApp" class="btn-bigger" @click="handleMaximize" />
          <icon-expand-content v-else class="btn-bigger" @click="handleMaximize" />
        </template>
        <icon-close class="btn-close" @click="handleClose()"></icon-close>
      </div>
    </header>
    <!-- /Header -->

    <select-chat-channel
      v-if="isAskingUserSelectChatChannel"
      @select="handleSelectChatChannel"
    ></select-chat-channel>

    <!-- Body and Footer -->
    <template v-if="liveMode && !isAskingUserSelectChatChannel">
      <chat-body
        :image-u-r-l="appStore.imageURL"
        :operator-name="operatorName"
        :hide-sign-in="hideSignIn"
        :choose-payment-method="choosePaymentMethod"
        :new-payment-method="newPaymentMethod"
        :toggle-menu="toggleMenu"
        :open-user-panel="openUserPanel"
        :widget-initial="widgetInitial"
      >
        <div
          v-if="showCookiesAccepted"
          class="w-full flex items-center"
          style="
            position: absolute;
            bottom: 0;
            left: 0;
            box-shadow: 0 -6px 9px rgba(0, 0, 0, 0.05);
            padding: 10px 15px;
          "
        >
          <icon-check-circle class="icon-primary-green icon-20 mr-2" />
          {{ $t('cookie_consent.cookies_accepted') }}
        </div>
      </chat-body>

      <cookie-consent
        v-if="showCookieConsent"
        @accept-all="handleAcceptAllCookies"
        @confirm="handleConfirmCookieSettings"
      ></cookie-consent>

      <footer v-if="!chatStore.loading" class="footer" :class="footerClass(formActive)">
        <form
          v-if="isUserOpen === false"
          :class="[
            'footer__inner',
            { active: formActive, 'footer__inner--shadow': !showCookiesAccepted }
          ]"
          @submit.prevent="sendMessage(inputMessage)"
        >
          <transition v-if="isButtonStyle" name="menu-up" appear>
            <chat-button-menu
              v-if="isMenuOpen"
              :menu-items="menuItems"
              :terms-of-use-link="termsOfUseLink"
              :privacy-policy-link="privacyPolicyLink"
              @close="toggleMenu"
              @menu-click="menuClick"
            ></chat-button-menu>
          </transition>

          <transition v-else :name="isRTL ? 'slide-right' : 'slide-left'" appear>
            <chat-menu
              v-if="isMenuOpen"
              :menu-items="menuItems"
              :terms-of-use-link="termsOfUseLink"
              :privacy-policy-link="privacyPolicyLink"
              @close="toggleMenu"
              @menu-click="menuClick"
            ></chat-menu>
          </transition>

          <div v-if="!isCustomBotTestMode" style="flex-shrink: 0">
            <icon-menu
              v-if="!isChatInputEnlarged"
              class="btn-menu"
              @click.stop="toggleMenu"
            ></icon-menu>

            <label
              v-if="isUploadFilesEnabled && !isChatInputEnlarged"
              :class="style['attach-file']"
            >
              <icon-attach-file></icon-attach-file>
              <input
                multiple
                accept=".xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf,.txt,.csv"
                type="file"
                @change="handleSubmitFiles"
              />
            </label>

            <label
              v-if="isUploadFilesEnabled && !isChatInputEnlarged"
              :class="style['attach-file']"
            >
              <icon-add-photo></icon-add-photo>
              <input accept="image/*" multiple type="file" @change="handleSubmitFiles" />
            </label>
          </div>

          <div v-if="isChatInputEnlarged" class="toggle-zone">
            <div class="toggle-icon" @click.prevent="toggleMode">
              <icon-arrow />
            </div>
          </div>

          <div class="chat-input">
            <textarea
              ref="tmessage"
              v-model="inputMessage"
              type="text"
              :placeholder="$t('message')"
              :class="{
                'big-mode': isChatInputEnlarged,
                'change-width': inputMessage.length >= 120,
                'small-text': $i18n.locale === 'ja'
              }"
              maxlength="160"
              rows="1"
              @focus="formActive = true"
              @blur="formActive = false"
              @input="userTyping"
              @paste="onPaste"
              @compositionstart="handleCompositionStart"
              @compositionend="handleCompositionEnd"
              @keydown="handleKeydown"
            ></textarea>

            <icon-send
              v-if="inputMessage.length <= limitOfText || isChatInputEnlarged"
              class="btn-send"
              :class="{
                'type-in': inputMessage.length >= 1,
                'enlarged-position': isChatInputEnlarged
              }"
              @click.prevent="sendMessage(inputMessage)"
            ></icon-send>
            <icon-circle-up
              v-if="!isChatInputEnlarged && inputMessage.length > limitOfText"
              class="btn-send"
              @click.prevent="toggleMode"
            ></icon-circle-up>
            <div v-if="inputMessage.length >= 120 && isChatInputEnlarged" class="text-count">
              {{ inputMessage.length }}/160
            </div>

            <button type="submit" style="display: none"></button>
          </div>
        </form>
      </footer>
    </template>
    <!-- /Body and Footer -->

    <users
      v-if="liveMode && userPanelStore.open"
      :user-props="userProps"
      :settings="settings"
      :chat-i-d="chatStore.chatId"
      :display-header.sync="displayHeader"
    />

    <redactor-view-page
      v-if="contentComponentInfo"
      :content-component-info="contentComponentInfo"
      @back="contentComponentInfo = null"
    />
    <details-page v-else-if="liveMode && detailPageTriggered" />

    <bottom-panel v-if="liveMode && bottomPanelComponent" />

    <portal-target name="chat-window"></portal-target>

    <t-modal transition="slide-left" :visible.sync="requestsModalVisible" fullscreen>
      <template #header>
        <h2 class="text-base">{{ $t('rev_requests.title') }}</h2>
      </template>
      <request-list @back="requestsModalVisible = false"></request-list>
    </t-modal>

    <chat-evaluation-modal
      :visible.sync="evaluationModalVisible"
      @like="handleEvaluateChat('like')"
      @dislike="handleEvaluateChat('dislike')"
      @close="closeChatWindow"
    ></chat-evaluation-modal>

    <!-- Maintenance -->
    <template v-if="maintenanceMode">
      <header class="chatbot-header" style="height: 46px">
        <icon-close class="btn-close" @click="toggleChat" />
      </header>
      <iframe
        v-if="maintenanceMode"
        style="
          border: none;
          height: 100vh;
          overflow: hidden;
          width: 100%;
          padding: 0 16px;
          background-color: #fff;
        "
        src="https://maintenance.tripla.ai/"
      />
    </template>
    <!-- /Maintenance -->
  </div>
</template>

<!-- NOTE: Not sure how to replace this image... -->

<script>
import ChatBody from './ChatBody.vue'
import ChatMenu from './ChatMenu.vue'
import Users from './Users/index.vue'
import DetailsPage from './Pages/DetailsPage.vue'
import RedactorViewPage from './Pages/RedactorViewPage.vue'
import {
  termsAndConditionURLForJA,
  termsAndConditionURL,
  privacyPolicyURLForJA,
  privacyPolicyURL,
  defaultOpName,
  defaultOpPosition,
  sendButtonImage,
  LocalStorageKeys,
  ChatChannels,
  CookieTypes
} from 'javascript/Constants'
import { EventBus } from 'ChatWidget/EventBus'
import BottomPanel from 'BottomPanel/index.vue'
import { mapState } from 'pinia'
import style from './layout.scss'
import { Chat, User } from 'src/apis'
import Url from 'url-parse'
import IconClose from 'assets/icons/close.svg?inline'
import IconMenu from 'assets/icons/menu.svg?inline'
import IconSend from 'assets/icons/send.svg?inline'
import IconAttachFile from 'assets/icons/attach-file.svg?inline'
import IconAddPhoto from 'assets/icons/add-photo.svg?inline'
import IconArrow from 'assets/icons/arrow-forward.svg?inline'
import IconCircleUp from 'assets/icons/expand-circle-up.svg?inline'
import IconCheckCircle from 'assets/icons/check-circle.svg?inline'
import IconExpandContent from 'assets/icons/expand_content.svg?inline'
import IconCollapseContent from 'assets/icons/collapse_content.svg?inline'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import RequestList from 'Pages/Requests/RequestList'
import ChatEvaluationModal from 'ChatWindow/Modals/ChatEvaluationModal'
import { useChatMenuStore } from 'Store/chat-menu'
import { useUserStore } from 'Store/user'
import { useAppStore } from 'Store/app'
import { useBottomPanelStore } from 'Store/bottom-panel'
import { useBrandChatbotStore } from 'Store/brand-chatbot'
import { useSettingsStore } from 'Store/settings'
import { useMaintenanceStore } from 'Store/maintenance'
import { useChatStore } from 'Store/chat'
import { useDetailsPageStore } from 'Store/details-page'
import { useUserPanelStore } from 'Store/user-panel'
import SelectChatChannel from 'ChatWindow/SelectChatChannel'
import CookieConsent from 'ChatWindow/CookieConsent'
import { useCookieConsentStore } from 'Store/cookie-consent'
import { UpdateCookiePreferences } from 'src/apis/User'
import getCurrentLocation from 'Utilities/Geolocation'
import { openBookingWidget } from 'Utilities/openBookingWidget'
import { showLoginView } from 'javascript/helpers/login'
import ChatButtonMenu from './ChatButtonMenu'

export default {
  setup() {
    return {
      chatMenuStore: useChatMenuStore(),
      userStore: useUserStore(),
      appStore: useAppStore(),
      bottomPanelStore: useBottomPanelStore(),
      brandChatbotStore: useBrandChatbotStore(),
      maintenanceStore: useMaintenanceStore(),
      chatStore: useChatStore(),
      settingsStore: useSettingsStore(),
      userPanelStore: useUserPanelStore(),
      cookieConsentStore: useCookieConsentStore(),
      detailsPageStore: useDetailsPageStore()
    }
  },

  components: {
    CookieConsent,
    SelectChatChannel,
    ChatEvaluationModal,
    RequestList,
    IconClose,
    IconMenu,
    IconSend,
    IconAttachFile,
    IconAddPhoto,
    IconArrow,
    IconCircleUp,
    IconCheckCircle,
    IconExpandContent,
    IconCollapseContent,
    BottomPanel,
    ChatBody,
    ChatMenu,
    ChatButtonMenu,
    Users,
    DetailsPage,
    RedactorViewPage
  },
  props: {
    toggleChat: { type: Function, required: true },
    widgetInitial: { type: Boolean, required: true }
  },
  data() {
    return {
      showCookieConsent: false,
      showCookiesAccepted: false,
      requestsModalVisible: false,
      evaluationModalVisible: false,
      isAskingUserSelectChatChannel: false,
      isDragover: false,
      datePickerIsOpen: false,
      displayHeader: true,
      defaultOpName,
      defaultOpPosition,
      disabledDates: {},
      formActive: false,
      isUserOpen: false,
      latitude: null,
      longitude: null,
      messageID: '',
      selectedUserWindow: '',
      sendImg: sendButtonImage,
      typingIndicatorTimeout: false,
      style,
      limitOfText: 1,
      isComposing: false,
      isChatInputEnlarged: false,
      maximizedApp: false,
      contentComponentInfo: null
    }
  },
  computed: {
    isRTL() {
      return this.$i18n.locale === 'ar'
    },
    ...mapState(useDetailsPageStore, {
      detailPageTriggered: 'isDetailsPageShown'
    }),
    ...mapState(useSettingsStore, {
      isFacilitySignInEnabled: 'getFacilitySignIn',
      settings: 'getSettings'
    }),
    ...mapState(useSettingsStore, [
      'isApp',
      'isAIOnlyFacility',
      'isCustomBotTestMode',
      'getSettings',
      'isUploadFilesEnabled'
    ]),

    chatHeader() {
      return this.getSettings[this.$i18n.locale].chat_header
    },

    chatCount() {
      return this.brandChatbotStore.chatCount
    },

    bottomPanelComponent() {
      return this.bottomPanelStore.component
    },

    isMenuOpen() {
      return this.chatMenuStore.open
    },
    isKnownUser() {
      return this.userStore.isKnownUser
    },

    isCSSLoaded() {
      return this.appStore.iframesHaveLoadedCSS.includes('chatwindow-app')
    },

    facilityName() {
      return this.settings[this.$i18n.locale].chat_header.operator_name
    },

    facilityOperator() {
      return this.settings[this.$i18n.locale].chat_header.operator_position
    },

    userProps() {
      const url = new Url(window.location, '/', true)
      const isTriplabotOpenByDefault = url.query.triplabot_open === 'true'

      let bwIframeParams = {}
      if (isTriplabotOpenByDefault) {
        bwIframeParams = {
          route: 'chat-sign-in'
        }
      }

      return {
        bwIframeParams,
        choosePaymentMethod: this.choosePaymentMethod,
        closeUserPanel: this.closeUserPanel,
        selectedUserWindow: this.selectedUserWindow,
        messageID: this.messageID // used to keep track of credit card widget messageID
      }
    },
    liveMode() {
      return !this.maintenanceStore.maintenance
    },
    maintenanceMode() {
      return this.maintenanceStore.maintenance
    },
    conciergeIcon() {
      let icon = this.appStore.conciergeIconStyle
      if (this.displayCount()) {
        icon = {
          ...icon,
          cursor: 'pointer'
        }
      }
      return icon
    },
    inputMessage: {
      get() {
        return this.appStore.inputMessage
      },
      set(message) {
        this.appStore.setInputMessage(message)
      }
    },
    operatorName() {
      return this.chatHeader.operator_name ? this.chatHeader.operator_name : defaultOpName
    },

    hideSignIn() {
      const shouldHideSignIn =
        (!this.isFacilitySignInEnabled && !this.settings.sign_in_enabled) ||
        this.appStore.hideSignIn
      return this.settings.business_type === 'restaurant' || shouldHideSignIn
    },
    menuItems() {
      return this.settingsStore.getSettings[this.$i18n.locale].menu_items
        .filter((menuItem) => {
          if (menuItem.action === 'login_and_signup') {
            if (this.hideSignIn || (this.isKnownUser && this.customLoginURL)) {
              return false
            }
          }
          if (menuItem.action === 'languages') {
            return this.settings.bot_languages.length > 0 && !this.isApp
          }
          return !!menuItem.text
        })
        .map((menuItem) => {
          if (menuItem.action === 'login_and_signup' && this.isKnownUser) {
            return {
              ...menuItem,
              text: this.$t('sign_out')
            }
          }
          return menuItem
        })
    },
    termsOfUseLink() {
      return this.$i18n.locale === 'ja' ? termsAndConditionURLForJA : termsAndConditionURL
    },
    privacyPolicyLink() {
      return this.$i18n.locale === 'ja' ? privacyPolicyURLForJA : privacyPolicyURL
    },
    isButtonStyle() {
      return this.settings.chatbot_menu_setting?.appearance === 'button'
    }
  },

  watch: {
    'cookieConsentStore.cookiePreferences.confirmed': {
      handler() {
        this.setCookieConsentVisible()
      },
      immediate: true
    }
  },

  created() {
    if (!triplaLocalStorage.getItem(LocalStorageKeys.SELECTED_CHAT_CHANNEL)) {
      this.isAskingUserSelectChatChannel = true
    }
  },

  mounted() {
    const url = new Url(window.location, '/', true)
    const isUserSignedIn = !!triplaLocalStorage.getItem('triplabot_known_user_signed_in')
    const isConfirmingEmail = !!url.query.triplabot_confirm_user && !isUserSignedIn
    if (isConfirmingEmail) {
      this.openUserPanel('BookingWidgetIframe')
    }

    // this event is fired from ChatWidget/App.vue when chat window is open
    EventBus.$on('focus-text-message', () => {
      if (this.maintenanceMode) return

      // Using time out because on the parent it uses display block and none (v-show), so hooks like activated() does not work.
      setTimeout(() => {
        if (this.$refs.tmessage) {
          this.$refs.tmessage.focus()
        }
      }, 1)
    })

    EventBus.$on('refresh-oauth-login', (json) => {
      if (this.maintenanceMode) return
      if (json.get_email) {
        this.openUserPanel('GetEmail')
      } else {
        this.closeUserPanel()
      }
      this.userStore.refreshLogin(json)
    })
  },

  methods: {
    handleMaximize() {
      this.maximizedApp = !this.maximizedApp
      EventBus.$emit('maximize-chat', this.maximizedApp)
    },
    handleCompositionStart() {
      this.isComposing = true
      this.evaluateChatInputSize()
    },

    handleCompositionEnd() {
      this.isComposing = false
      this.evaluateChatInputSize()
    },

    evaluateChatInputSize() {
      if (
        this.inputMessage.length >= this.limitOfText ||
        this.isComposing ||
        this.inputMessage.includes('\n')
      ) {
        this.isChatInputEnlarged = true
      } else {
        this.isChatInputEnlarged = false
      }
    },

    handleKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey && !this.isComposing) {
        event.preventDefault()
        this.sendMessage(this.inputMessage)
        this.isChatInputEnlarged = false
      }
    },

    setCookieConsentVisible() {
      this.showCookieConsent =
        this.settingsStore.isCookieConsentEnabled &&
        !this.cookieConsentStore.isCookiePreferencesConfirmed
    },

    displayCookiesAccepted() {
      this.showCookiesAccepted = true
      setTimeout(() => {
        this.showCookiesAccepted = false
      }, 600)
    },

    handleConfirmCookieSettings(cookies) {
      this.cookieConsentStore.setCookiePreferences(cookies)
      this.showCookieConsent = false
      this.displayCookiesAccepted()
      UpdateCookiePreferences(this.cookieConsentStore.cookiePreferences)

      if (cookies.includes(CookieTypes.FUNCTIONAL)) {
        User.AcceptTermsConditions(this.settingsStore.getHotelId)
      }
    },

    handleAcceptAllCookies() {
      this.showCookieConsent = false
      this.displayCookiesAccepted()
      this.cookieConsentStore.acceptAllCookies()
      UpdateCookiePreferences(this.cookieConsentStore.cookiePreferences)
      User.AcceptTermsConditions(this.settingsStore.getHotelId)
    },

    handleSelectChatChannel(channel) {
      this.appStore.setChatChannel(channel)

      if (channel === ChatChannels.WEB) {
        this.isAskingUserSelectChatChannel = false
      } else {
        this.toggleChat()
        this.appStore.openChatChannel(channel)
      }
    },

    handleEvaluateChat(evaluation) {
      this.evaluationModalVisible = false
      Chat.sendEvaluation(this.chatStore.chatId, evaluation)

      this.closeChatWindow()
    },

    handleClose() {
      this.$gtag.trackEvent('Chatbot close icon', 'Chat close')

      const lastChatEvaluatedAt = new Date(
        triplaLocalStorage.getItem(LocalStorageKeys.LAST_CHAT_EVALUADTED_AT)
      )

      const shouldShowEvaluation =
        !lastChatEvaluatedAt ||
        Number.isNaN(lastChatEvaluatedAt.valueOf()) ||
        lastChatEvaluatedAt.toISOString().slice(0, 10) !== new Date().toISOString().slice(0, 10)
      if (
        !this.isCustomBotTestMode &&
        shouldShowEvaluation &&
        !this.isAskingUserSelectChatChannel
      ) {
        // only show it once a day
        this.evaluationModalVisible = true
        triplaLocalStorage.setItem(
          LocalStorageKeys.LAST_CHAT_EVALUADTED_AT,
          new Date().toISOString()
        )
        return
      }

      this.closeChatWindow()
    },

    closeChatWindow() {
      window.parent.postMessage('triplabot-close', '*')
      this.toggleChat()
    },

    onPaste(event) {
      if (event.clipboardData.files && event.clipboardData.files.length) {
        event.preventDefault()
        event.stopPropagation()
        this.sendFiles(event.clipboardData.files)
      }
    },

    onDrop(event) {
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = 'copy'
      this.isDragover = false

      if (this.isUserOpen || this.detailPageTriggered || this.bottomPanelComponent) return
      this.sendFiles(event.dataTransfer.files)
    },

    onDragover(event) {
      event.preventDefault()
      event.stopPropagation()
      this.isDragover = true
    },

    onDragleave(event) {
      event.preventDefault()
      event.stopPropagation()
      this.isDragover = false
    },

    handleSubmitFiles(event) {
      this.sendFiles(event.target.files)
    },

    sendFiles(files) {
      this.chatStore.sendFiles(Array.from(files))
    },

    sendMessage(msg) {
      this.isChatInputEnlarged = false
      return Chat.sendMessage(msg, { isManualFromUser: true })
    },
    newPaymentMethod(messageID = '') {
      if (messageID) this.messageID = messageID
      this.openUserPanel('NewPaymentMethod')
    },
    choosePaymentMethod(paymentProps, message, messageID = '') {
      // TODO: need to find a good way of getting messageID to disable the widget in backend
      if (this.chatStore.chatId) {
        Chat.choosePaymentMethod(this.chatStore.chatId, {
          message,
          message_action: JSON.stringify(paymentProps)
        })
      }
    },
    toggleMenu() {
      this.chatMenuStore.toggle()
      this.$gtag.trackEvent(this.isMenuOpen ? 'Open' : 'Close', 'Chat menu', null)
    },
    clickOutside(e) {
      if (
        this.isMenuOpen &&
        e.target.id !== 'menu-btn' &&
        e.target.classList.contains('menu-button')
      ) {
        this.chatMenuStore.setOpen(false)
      }
    },
    openUserPanel(panel = '') {
      this.userPanelStore.openPanel(panel)
    },
    closeUserPanel() {
      this.userPanelStore.setOpen(false)
    },

    closeDateWidget() {
      this.datePickerIsOpen = false
    },
    userStopTyping() {
      this.typingIndicatorTimeout = setTimeout(() => {
        this.chatStore.publishTyping('stop')
        this.typingIndicatorTimeout = false
      }, 4000)
    },
    detectInputLanguage(input) {
      if (/[\u3040-\u30ff\u3400-\u4DBF\u4E00-\u9FFF]/.test(input)) {
        return 'jp'
      } else if (/[\uac00-\ud7a3]/.test(input)) {
        return 'ko'
      } else if (/[A-Za-z]/.test(input)) {
        return 'en'
      }
      return this.$i18n.locale
    },
    setLimitOfText(language) {
      const limits = { en: 22, jp: 12, ko: 14 }
      this.limitOfText = limits[language] || this.limitOfText
    },
    userTyping() {
      const language = this.detectInputLanguage(this.inputMessage)
      this.setLimitOfText(language)
      this.isChatInputEnlarged = this.inputMessage.length >= this.limitOfText
      this.evaluateChatInputSize()
      if (this.typingIndicatorTimeout) {
        clearTimeout(this.typingIndicatorTimeout)
        this.userStopTyping()
      } else {
        EventBus.$emit('publish-typing', 'start')
        this.userStopTyping()
      }
    },
    toggleMode() {
      this.isChatInputEnlarged = !this.isChatInputEnlarged
    },
    footerClass(formActive) {
      let footerCssClass = formActive ? 'active' : ''
      if (this.settings.footer === 'gurunavi') {
        footerCssClass = footerCssClass + ' powered-by-gurunavi'
      }
      return footerCssClass
    },
    switchBackToBranch() {
      this.chatStore.changeChat(triplaLocalStorage.getItem('original_brand_hotel_code'))
    },
    isBranchChat() {
      return (
        !this.brandChatbotStore.isBrand && triplaLocalStorage.getItem('original_brand_hotel_code')
      )
    },
    popupBranchSelector() {
      this.displayCount() && Chat.TriggerVisitedBranchList(this.chatStore.chatId).then((res) => {})
    },
    displayCount() {
      return !!this.chatCount && !this.isBranchChat()
    },
    menuClick(menu) {
      if (menu.action === 'directions') {
        getCurrentLocation().then((position) => {
          const options = {
            messageAction: menu.action,
            resetValue: menu.reset,
            useAi: menu.use_ai,
            ...position
          }
          Chat.sendMessage(menu.text, options)
        })
      } else if (menu.action === 'custom_bot_requests') {
        this.requestsModalVisible = true
      } else if (menu.action === 'room_reservation') {
        this.$gtag.recordChatMenuRoomReservationTriggered(menu)
        openBookingWidget(menu)
      } else if (menu.action === 'room_reservation_confirmation') {
        openBookingWidget({ fromChatbot: 'bookingConfirm' }, 'myReservation')
      } else if (menu.action === 'languages') {
        this.showLanguageMenu()
      } else if (menu.action === 'login_and_signup') {
        if (this.isKnownUser) {
          this.openUserPanel('SignOut')
        } else {
          showLoginView()
        }
      } else if (menu.action === 'link') {
        window.open(menu.shared_content_component.url, '_blank')
      } else if (menu.action === 'rich_content') {
        this.contentComponentInfo = {
          title: menu.text,
          ...menu.shared_content_component
        }
      } else if (menu.action === 'custom_bot') {
        const options = {
          messageAction: 'custom_bot',
          messageActionId: menu.shared_content_component.auto_message_id,
          autoMessageType: 'menu',
          isManualFromUser: false,
          resetValue: menu.reset
        }
        Chat.sendMessage(menu.text, options)
      } else {
        const options = {
          messageAction: menu.action,
          resetValue: menu.reset,
          useAi: menu.use_ai,
          resetValue: menu.reset
        }
        Chat.sendMessage(menu.text, options)
      }
      this.$gtag.trackEvent('Menu item click', 'Chat menu', menu.action)
    },
    showLanguageMenu() {
      this.settingsStore.toggleLanguageMenu(true)
      EventBus.$emit('show-language-menu')
    }
  }
}
</script>
